import { useConfigStore } from '@/useConfigStore';

export type SyncPayload = {
  settings?: unknown;
  timer?: unknown;
  tasks?: unknown;
};

export type UserDataResponse = {
  message: string;
  data: unknown;
};

export type SyncResponse = {
  message: string;
  result: unknown;
};

export type SessionLog = {
  id: string;
  completed: boolean;
  mode: 'pomodoro' | 'shortBreak' | 'longBreak';
  time: number;
  timestamp: string;
};

export type TaskStats = {
  totalTasks: number;
  completedTasks: number;
  totalPomodorosEstimated: number;
  totalPomodorosCompleted: number;
  completionRate: number;
};

export type StatisticsPayload = {
  streak: number;
  days_access: number;
  total_hours: number;
  task_stats: TaskStats;
};

export type StatisticsResponse = {
  message: string;
  logs: SessionLog[];
  statistics: StatisticsPayload;
};

export type RankingEntry = {
  name: string;
  streak: number;
  total_hours: number;
};

export type RankingResponse = {
  message: string;
  ranking: RankingEntry[];
};

const getServerUrl = (): string => {
  const { config } = useConfigStore.getState();
  if (!config?.SERVER_URL) {
    throw new Error('Server URL not configured. Make sure config.json is loaded.');
  }
  return config.SERVER_URL;
};

const getUid = (): string | null => {
  if (typeof window === 'undefined') return null;

  const user = localStorage.getItem('pomozid_user');
  if (!user) return null;

  try {
    const parsed = JSON.parse(user);
    return parsed?.id ?? null;
  } catch {
    return null;
  }
};

const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('pomozid_token');
};

const buildAuthHeaders = (includeContentType = false): HeadersInit => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('User is not authenticated.');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const raw = await response.text();
  let data: any = raw;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    // fall back to raw text
  }

  if (!response.ok) {
    const message =
      (data && (data.error || data.message)) ||
      raw ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data as T;
};

export const fetchUserData = async (): Promise<UserDataResponse | null> => {
  const serverUrl = getServerUrl();
  const uid = getUid();
  if (!uid) {
    throw new Error('Unable to determine user id.');
  }

  const res = await fetch(`${serverUrl}/user-data?id=${uid}`, {
    headers: buildAuthHeaders(),
  });

  return parseResponse<UserDataResponse>(res);
};

export const syncUserData = async (data: SyncPayload): Promise<SyncResponse> => {
  const serverUrl = getServerUrl();
  const uid = getUid();
  if (!uid) {
    throw new Error('Unable to determine user id.');
  }

  const res = await fetch(`${serverUrl}/user-data/sync`, {
    method: 'POST',
    headers: buildAuthHeaders(true),
    body: JSON.stringify({ uid, data }),
  });

  return parseResponse<SyncResponse>(res);
};

export const fetchUserStatistics = async (): Promise<StatisticsResponse> => {
  const serverUrl = getServerUrl();
  const uid = getUid();
  if (!uid) {
    throw new Error('Unable to determine user id.');
  }

  const res = await fetch(`${serverUrl}/user-statistics?id=${uid}`, {
    headers: buildAuthHeaders(),
  });

  return parseResponse<StatisticsResponse>(res);
};

export const fetchRanking = async (): Promise<RankingResponse> => {
  const serverUrl = getServerUrl();

  const res = await fetch(`${serverUrl}/ranking`, {
    headers: buildAuthHeaders(),
  });

  return parseResponse<RankingResponse>(res);
};

export const logAuthEvent = async (
  event: 'signin' | 'signout',
  payload: Record<string, unknown>,
): Promise<void> => {
  const serverUrl = getServerUrl();

  const res = await fetch(`${serverUrl}/auth/${event}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  await parseResponse(res);
};
